import { z } from 'zod';

/**
 * The filter language used by every list endpoint in the product.
 *
 * The transport schema below is deliberately permissive about `value`: the same
 * operator carries a different value shape depending on the *field's* declared
 * type, which the transport layer cannot know. Strict validation happens on the
 * server, where the resource's `FilterSchema` supplies the field type and can
 * produce a precise error such as "operator `contains` is not valid for the
 * numeric field `id`".
 *
 * See `docs/04-api/filtering.md` for the full operator/type matrix.
 */

// ---------------------------------------------------------------------------
// Operators
// ---------------------------------------------------------------------------

export const FILTER_OPERATORS = [
  // Equality
  'is',
  'is_not',
  // Set membership — "Status is any of Open, Pending"
  'in',
  'not_in',
  // Text
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  // Ordering (numbers, dates, durations)
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  // Date-specific aliases, kept distinct so the UI can label them naturally
  'before',
  'after',
  'on',
  // Presence
  'is_empty',
  'is_not_empty',
  // Collections — tags, multi-select custom fields, group membership
  'has_any',
  'has_all',
  'has_none',
] as const;

export const FilterOperatorSchema = z.enum(FILTER_OPERATORS);
export type FilterOperator = z.infer<typeof FilterOperatorSchema>;

/** Operators that take no value at all. */
export const VALUELESS_OPERATORS = ['is_empty', 'is_not_empty'] as const satisfies readonly FilterOperator[];

/** Operators whose value must be an array. */
export const ARRAY_OPERATORS = [
  'in',
  'not_in',
  'between',
  'has_any',
  'has_all',
  'has_none',
] as const satisfies readonly FilterOperator[];

// ---------------------------------------------------------------------------
// Relative dates
// ---------------------------------------------------------------------------

/**
 * Named calendar windows, resolved against the *viewer's* timezone at query
 * time. Saved views store the expression, not the resolved range, so
 * "Created this week" keeps meaning this week.
 */
export const RELATIVE_DATE_PRESETS = [
  'today',
  'yesterday',
  'tomorrow',
  'this_week',
  'last_week',
  'next_week',
  'this_month',
  'last_month',
  'next_month',
  'this_quarter',
  'last_quarter',
  'this_year',
  'last_year',
] as const;

export const RelativeDatePresetSchema = z.enum(RELATIVE_DATE_PRESETS);
export type RelativeDatePreset = z.infer<typeof RelativeDatePresetSchema>;

export const DateUnitSchema = z.enum(['minute', 'hour', 'day', 'week', 'month', 'year']);
export type DateUnit = z.infer<typeof DateUnitSchema>;

/** `{ relative: 'this_week' }` */
export const RelativeDateSchema = z.object({ relative: RelativeDatePresetSchema });

/** `{ rolling: { direction: 'past', amount: 7, unit: 'day' } }` — the last 7 days. */
export const RollingDateSchema = z.object({
  rolling: z.object({
    direction: z.enum(['past', 'next']),
    amount: z.number().int().min(1).max(3650),
    unit: DateUnitSchema,
  }),
});

export const DateExpressionSchema = z.union([RelativeDateSchema, RollingDateSchema]);
export type DateExpression = z.infer<typeof DateExpressionSchema>;

export function isDateExpression(value: unknown): value is DateExpression {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('relative' in value || 'rolling' in value)
  );
}

// ---------------------------------------------------------------------------
// Special value tokens
// ---------------------------------------------------------------------------

/**
 * `"@me"` resolves to the requesting actor. It exists so a saved view shared
 * across a team ("My open tickets") means something different, and correct, for
 * each viewer. Resolved server-side; never trusted from the client as an id.
 */
export const CURRENT_ACTOR_TOKEN = '@me';

/** `"@my_groups"` resolves to the set of groups the actor belongs to. */
export const CURRENT_ACTOR_GROUPS_TOKEN = '@my_groups';

// ---------------------------------------------------------------------------
// Filter tree
// ---------------------------------------------------------------------------

export const FilterConditionSchema = z.object({
  /** Field key, resolved against the resource's whitelist. Never interpolated into SQL. */
  field: z.string().min(1).max(128),
  op: FilterOperatorSchema,
  /** Shape depends on the field's declared type; validated server-side. */
  value: z.unknown().optional(),
});
export type FilterCondition = z.infer<typeof FilterConditionSchema>;

export type FilterNode = FilterCondition | FilterGroup;
export interface FilterGroup {
  logic: 'and' | 'or';
  nodes: FilterNode[];
}

/**
 * Recursive group. Depth and total condition count are bounded by the server
 * (see `FILTER_LIMITS`) so a hostile client cannot force a pathological query.
 */
export const FilterGroupSchema: z.ZodType<FilterGroup> = z.lazy(() =>
  z.object({
    logic: z.enum(['and', 'or']),
    nodes: z.array(z.union([FilterConditionSchema, FilterGroupSchema])).min(1).max(50),
  }),
);

export const FilterNodeSchema: z.ZodType<FilterNode> = z.lazy(() =>
  z.union([FilterConditionSchema, FilterGroupSchema]),
);

export const FILTER_LIMITS = {
  /** Deepest permitted nesting of AND/OR groups. */
  maxDepth: 5,
  /** Total conditions across the whole tree. */
  maxConditions: 60,
  /** Elements in an `in` / `has_any` array. */
  maxArrayValues: 500,
} as const;

export function isFilterGroup(node: FilterNode): node is FilterGroup {
  return 'logic' in node;
}

/** Counts every leaf condition in a tree. Used to enforce `FILTER_LIMITS`. */
export function countConditions(node: FilterNode): number {
  return isFilterGroup(node) ? node.nodes.reduce((n, c) => n + countConditions(c), 0) : 1;
}

/** Depth of the deepest group. A bare condition has depth 0. */
export function filterDepth(node: FilterNode): number {
  return isFilterGroup(node) ? 1 + Math.max(0, ...node.nodes.map(filterDepth)) : 0;
}

// ---------------------------------------------------------------------------
// Sorting, pagination, and the list envelope
// ---------------------------------------------------------------------------

export const SortDirectionSchema = z.enum(['asc', 'desc']);
export type SortDirection = z.infer<typeof SortDirectionSchema>;

export const SortSpecSchema = z.object({
  field: z.string().min(1).max(128),
  direction: SortDirectionSchema.default('asc'),
  /** Where NULLs land. Defaults per field type; explicit here when it matters. */
  nulls: z.enum(['first', 'last']).optional(),
});
export type SortSpec = z.infer<typeof SortSpecSchema>;

export const PAGINATION_DEFAULTS = { page: 1, perPage: 30, maxPerPage: 200 } as const;

export const PaginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(PAGINATION_DEFAULTS.page),
  per_page: z.coerce
    .number()
    .int()
    .min(1)
    .max(PAGINATION_DEFAULTS.maxPerPage)
    .default(PAGINATION_DEFAULTS.perPage),
});
export type Pagination = z.infer<typeof PaginationSchema>;

/**
 * The canonical list request. Every collection endpoint accepts this shape as
 * JSON on `POST /<resource>/search`, and a flattened form on `GET /<resource>`
 * for simple cases and shareable URLs.
 */
export const ListQuerySchema = z.object({
  filter: FilterNodeSchema.optional(),
  /** Free-text query, applied across the resource's searchable fields. */
  q: z.string().trim().max(512).optional(),
  sort: z.array(SortSpecSchema).max(4).optional(),
  page: z.coerce.number().int().min(1).default(PAGINATION_DEFAULTS.page),
  per_page: z.coerce
    .number()
    .int()
    .min(1)
    .max(PAGINATION_DEFAULTS.maxPerPage)
    .default(PAGINATION_DEFAULTS.perPage),
  /** Extra related entities to embed, e.g. `["requester","assignee"]`. */
  include: z.array(z.string().max(64)).max(20).optional(),
  /** Restrict the payload to these fields. Reduces bandwidth on wide tables. */
  fields: z.array(z.string().max(64)).max(100).optional(),
});
export type ListQuery = z.infer<typeof ListQuerySchema>;

export interface PageMeta {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface ListResponse<T> {
  data: T[];
  meta: PageMeta;
  /** Echo of what the server actually applied, after normalisation. */
  applied?: {
    filter?: FilterNode;
    q?: string;
    sort?: SortSpec[];
  };
}

export function pageMeta(page: number, perPage: number, total: number): PageMeta {
  const totalPages = perPage > 0 ? Math.ceil(total / perPage) : 0;
  return {
    page,
    per_page: perPage,
    total,
    total_pages: totalPages,
    has_next: page < totalPages,
    has_prev: page > 1,
  };
}

// ---------------------------------------------------------------------------
// Filter metadata — what the UI needs to render a filter builder
// ---------------------------------------------------------------------------

export const FILTER_FIELD_TYPES = [
  'string',
  'text',
  'number',
  'decimal',
  'boolean',
  'date',
  'datetime',
  'enum',
  'reference',
  'reference_array',
  'tags',
  'duration',
] as const;

export const FilterFieldTypeSchema = z.enum(FILTER_FIELD_TYPES);
export type FilterFieldType = z.infer<typeof FilterFieldTypeSchema>;

export const FilterFieldMetaSchema = z.object({
  field: z.string(),
  label: z.string(),
  type: FilterFieldTypeSchema,
  operators: z.array(FilterOperatorSchema),
  /** Present for `enum`: the allowed values with display labels. */
  choices: z.array(z.object({ value: z.union([z.string(), z.number()]), label: z.string() })).optional(),
  /** Present for `reference`: the resource to look up, e.g. `agents`. */
  references: z.string().optional(),
  /** True when the field came from `field_definitions` rather than the core schema. */
  custom: z.boolean().default(false),
  sortable: z.boolean().default(true),
  /** Grouping hint for the filter panel, e.g. "Ticket properties", "SLA". */
  group: z.string().optional(),
});
export type FilterFieldMeta = z.infer<typeof FilterFieldMetaSchema>;

export const ResourceFilterMetaSchema = z.object({
  resource: z.string(),
  fields: z.array(FilterFieldMetaSchema),
});
export type ResourceFilterMeta = z.infer<typeof ResourceFilterMetaSchema>;

/**
 * Operators offered for each field type. The server enforces this; the client
 * uses it to build the operator dropdown. Keeping one table means the UI and the
 * compiler can never disagree about what is legal.
 */
export const OPERATORS_BY_TYPE: Record<FilterFieldType, readonly FilterOperator[]> = {
  string: ['is', 'is_not', 'contains', 'not_contains', 'starts_with', 'ends_with', 'in', 'not_in', 'is_empty', 'is_not_empty'],
  text: ['contains', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'],
  number: ['is', 'is_not', 'gt', 'gte', 'lt', 'lte', 'between', 'in', 'not_in', 'is_empty', 'is_not_empty'],
  decimal: ['is', 'is_not', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'],
  boolean: ['is'],
  date: ['is', 'is_not', 'before', 'after', 'on', 'between', 'is_empty', 'is_not_empty'],
  datetime: ['is', 'is_not', 'before', 'after', 'on', 'between', 'is_empty', 'is_not_empty'],
  enum: ['is', 'is_not', 'in', 'not_in', 'is_empty', 'is_not_empty'],
  reference: ['is', 'is_not', 'in', 'not_in', 'is_empty', 'is_not_empty'],
  reference_array: ['has_any', 'has_all', 'has_none', 'is_empty', 'is_not_empty'],
  tags: ['has_any', 'has_all', 'has_none', 'is_empty', 'is_not_empty'],
  duration: ['gt', 'gte', 'lt', 'lte', 'between'],
};
